import {
  Show,
  Text,
  Stack,
  HStack,
  Button,
  type HTMLChakraProps,
} from '@react/chakra';
import {
  useMemo,
  useState,
  cloneElement,
  type JSX,
  type ReactElement,
} from 'react';

import type { IIconProps } from '@react/chakra/core';

import { Link } from '~/components';

import type { IChapterProps } from '../../../interface';

interface IChapterButtonProps {
  type?: 'next' | 'previous';
  data: IChapterProps | null;
  icon?: ReactElement<HTMLChakraProps<'svg', IIconProps>>;
}

export function ChapterButton({
  data,
  icon,
  type = 'previous',
}: IChapterButtonProps): JSX.Element {
  const [hovered, setHovered] = useState(false);

  const iconComponent = useMemo(() => {
    if (!icon) {
      return null;
    }

    return cloneElement(icon, {
      fill: 'white',
      transition: 'all 0.3s',
      w: hovered ? '40px' : 0,
      h: hovered ? '40px' : 0,
    });
  }, [hovered, icon]);

  const hover = () => {
    setHovered(true);
  };

  const leave = () => {
    setHovered(false);
  };

  return (
    <Button
      py={5}
      px={8}
      asChild
      h='unset'
      color='white'
      borderRadius='8px'
      bgColor='gray.700'
      onMouseEnter={hover}
      onMouseLeave={leave}
      w={{
        base: '100%',
        md: 'unset',
      }}
      mt={{
        base: type === 'previous' ? 0 : 5,
        md: 0,
      }}
      _hover={{
        bgColor: 'gray.600',
      }}
    >
      <Link
        noUnderline
        href={`/posts/${data?.slug}`}
        containerProps={{
          textAlign: 'center',
        }}
      >
        <HStack gap={hovered ? 5 : 0}>
          <Show when={type === 'previous'}>{iconComponent}</Show>
          <Stack gap={4}>
            <Text>{data?.title}</Text>
            <Text fontSize='14px'>Chapter {data?.chapter}</Text>
          </Stack>
          <Show when={type === 'next'}>{iconComponent}</Show>
        </HStack>
      </Link>
    </Button>
  );
}
